// Which backend the app talks to. Local development uses the emulators and
// every build uses staging unless VITE_GUILDITE_ENV=production is set on
// purpose: only the go-live build (docs/v2/CUTOVER.md) sets it, after the v2
// Worker, rules and migrations are live. Nothing chooses production by default.
export type AppEnvironment = 'emulator' | 'staging' | 'production';

export type FirebaseWebConfig = {
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket?: string;
  messagingSenderId?: string;
  appId?: string;
  databaseURL: string;
};

export type EnvironmentConfig = {
  name: AppEnvironment;
  firebase: FirebaseWebConfig;
  workerUrl: string;
  emulators: null | { auth: string; firestore: { host: string; port: number }; database: { host: string; port: number } };
  // Public Web Push key (the live app uses the same one; the private half is a Worker secret).
  vapidPublicKey: string;
};

export const EMULATOR_PROJECT_ID = 'demo-guildite';

const ENVIRONMENTS: Record<AppEnvironment, EnvironmentConfig> = {
  emulator: {
    name: 'emulator',
    firebase: {
      apiKey: 'demo-api-key',
      authDomain: `${EMULATOR_PROJECT_ID}.firebaseapp.com`,
      projectId: EMULATOR_PROJECT_ID,
      // connectDatabaseEmulator keeps this namespace and swaps in the local host.
      databaseURL: `https://${EMULATOR_PROJECT_ID}-default-rtdb.firebaseio.com`,
    },
    workerUrl: 'http://127.0.0.1:8787',
    vapidPublicKey: 'BMv9xTEcda4Ek665cG0Vr6zogcmg7RmKZf9_-SeRfecr3pNnU1SHRDQfE-NpLd20cYB5_nYxY_qGJIRuhi5a014',
    emulators: {
      auth: 'http://127.0.0.1:9099',
      firestore: { host: '127.0.0.1', port: 8080 },
      database: { host: '127.0.0.1', port: 9000 },
    },
  },
  // Public web configuration, identical to js/config/firebase.js and
  // js/config/endpoints.js for staging.
  staging: {
    name: 'staging',
    firebase: {
      apiKey: 'AIzaSyCNfc6dU0RHTPkrcDNdenOnGDMFaQELC9U',
      authDomain: 'guildite-staging.firebaseapp.com',
      projectId: 'guildite-staging',
      storageBucket: 'guildite-staging.firebasestorage.app',
      messagingSenderId: '803260805306',
      appId: '1:803260805306:web:8aae8eb306cbdf824ad283',
      databaseURL: 'https://guildite-staging-default-rtdb.europe-west1.firebasedatabase.app',
    },
    workerUrl: 'https://guildite-notifications-staging.gildaig3.workers.dev',
    vapidPublicKey: 'BMv9xTEcda4Ek665cG0Vr6zogcmg7RmKZf9_-SeRfecr3pNnU1SHRDQfE-NpLd20cYB5_nYxY_qGJIRuhi5a014',
    emulators: null,
  },
  // Public web configuration, identical to js/config/firebase.js and
  // js/config/endpoints.js for production.
  production: {
    name: 'production',
    firebase: {
      apiKey: 'AIzaSyBzPa671GH71UvTcZ3dECFPrW4xe1vS9ds',
      authDomain: 'marketplace-e0bff.firebaseapp.com',
      projectId: 'marketplace-e0bff',
      storageBucket: 'marketplace-e0bff.appspot.com',
      messagingSenderId: '892936444768',
      appId: '1:892936444768:web:f595b3a3e5d697988e1c05',
      databaseURL: 'https://marketplace-e0bff-default-rtdb.europe-west1.firebasedatabase.app',
    },
    workerUrl: 'https://guildite-notifications.gildaig3.workers.dev',
    vapidPublicKey: 'BMv9xTEcda4Ek665cG0Vr6zogcmg7RmKZf9_-SeRfecr3pNnU1SHRDQfE-NpLd20cYB5_nYxY_qGJIRuhi5a014',
    emulators: null,
  },
};

export function environmentConfig(name: string | undefined, isDev: boolean): EnvironmentConfig {
  const selected = name || (isDev ? 'emulator' : 'staging');
  if (selected !== 'emulator' && selected !== 'staging' && selected !== 'production') {
    throw new Error(`Unknown VITE_GUILDITE_ENV "${selected}". Use "emulator", "staging" or "production".`);
  }
  // The dev server never talks to production.
  if (selected === 'production' && isDev) throw new Error('VITE_GUILDITE_ENV=production is for go-live builds only, not the dev server.');
  return ENVIRONMENTS[selected];
}

export const env = environmentConfig(import.meta.env.VITE_GUILDITE_ENV, import.meta.env.DEV);

// Phone sign-in (DECISIONS, 21 October) is built but stays off until the
// founder chooses how the codes are texted. The emulators test it without
// sending texts; VITE_PHONE_SIGN_IN=on turns it on for a build.
export const phoneSignInEnabled = env.name === 'emulator' || import.meta.env.VITE_PHONE_SIGN_IN === 'on';

// Shop handover and payout accounts per shop (HANDOFF batch C2) stay off until
// swap day. The emulators try them; VITE_SHOP_OWNERSHIP_V2=on turns them on for
// a build (the Worker also needs SHOP_OWNERSHIP_V2=on).
export const shopOwnershipEnabled = env.name === 'emulator' || import.meta.env.VITE_SHOP_OWNERSHIP_V2 === 'on';

export const googleMapsKey = String(import.meta.env.VITE_GOOGLE_MAPS_KEY || '');
export const googleMapsMapId = String(import.meta.env.VITE_GOOGLE_MAPS_MAP_ID || 'DEMO_MAP_ID');

// Where the music library's files are served from (MEDIA_EDITOR_PLAN step 6):
// `${musicBase}/${track.id}.mp3`. Unset until the files are hosted; the video
// editor then offers your own recorded sound only.
export const musicBase = String(import.meta.env.VITE_MUSIC_BASE || '').replace(/\/$/, '') || null;
